import { useTranslations } from '@aikoa/i18n/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useEffect, useState } from 'react'
import { View } from 'react-native'
import { formatIntervalToPostgres, parsePostgresInterval } from '~/components/admin/IntervalInput'
import {
  buildPromotionDataFromForm,
  getDefaultPromotionFormValues,
  isPromotionFormValid,
  parsePromotionDataToForm,
  PromotionForm,
  type PromotionFormValues,
  type PromotionType,
} from '~/components/admin/PromotionForm'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { Body, Label } from '~/components/typography/Content'
import { Heading2 } from '~/components/typography/Titles'
import { Button, ButtonLabel } from '~/components/ui/button'
import { queryClient, trpc } from '~/lib/trpc'

function invalidatePromotionQueries(promotionId: string) {
  void queryClient.invalidateQueries({
    queryKey: ['admin', 'listPromotions'],
  })
  void queryClient.invalidateQueries({
    queryKey: ['admin', 'getPromotion', { promotionId }],
  })
  void queryClient.invalidateQueries({
    queryKey: trpc.admin.getPromotion.queryKey({ promotionId }),
  })
}

export default function EditPromotion() {
  const { t } = useTranslations('admin')
  const router = useRouter()
  const { promotionId } = useLocalSearchParams<{ promotionId: string }>()

  const [values, setValues] = useState<PromotionFormValues>(getDefaultPromotionFormValues())

  const {
    data: promotion,
    isLoading,
    error,
  } = useQuery(trpc.admin.getPromotion.queryOptions({ promotionId: promotionId! }))

  const isDeactivated =
    promotion?.deactivated_at !== null && promotion?.deactivated_at !== undefined

  useEffect(() => {
    if (promotion) {
      setValues({
        promotionCode: promotion.promotion_code,
        titleKey: promotion.title_key,
        promotionAmount: String(promotion.promotion_amount),
        promotionType: promotion.promotion_type as PromotionType,
        validDuration: parsePostgresInterval(promotion.valid_duration as string | null),
        maxActivations: promotion.max_activations !== null ? String(promotion.max_activations) : '',
        referralGeneration: parsePromotionDataToForm(
          promotion.promotion_data as Record<string, unknown> | null
        ),
      })
    }
  }, [promotion])

  const updateMutation = useMutation(
    trpc.admin.updatePromotion.mutationOptions({
      onSuccess: () => {
        invalidatePromotionQueries(promotionId!)
        router.back()
      },
    })
  )

  const deactivateMutation = useMutation(
    trpc.admin.deactivatePromotion.mutationOptions({
      onSuccess: () => {
        invalidatePromotionQueries(promotionId!)
      },
    })
  )

  const reactivateMutation = useMutation(
    trpc.admin.reactivatePromotion.mutationOptions({
      onSuccess: () => {
        invalidatePromotionQueries(promotionId!)
      },
    })
  )

  const isToggling = deactivateMutation.isPending || reactivateMutation.isPending

  if (isLoading) {
    return (
      <ScreenView>
        <ContentBox className="flex-1" variant="transparent">
          <Body className="text-center text-neutral-500">
            {t(($) => $.promotions.edit.loading)}
          </Body>
        </ContentBox>
      </ScreenView>
    )
  }

  if (error || !promotion) {
    return (
      <ScreenView>
        <ContentBox className="flex-1" variant="transparent">
          <Body className="text-center text-red-600">{t(($) => $.promotions.edit.loadError)}</Body>
        </ContentBox>
      </ScreenView>
    )
  }

  return (
    <ScreenView>
      <ContentBox className="flex-1" contentContainerClassName="gap-6" variant="transparent">
        <Heading2 className="text-center">{t(($) => $.promotions.edit.title)}</Heading2>

        {isDeactivated && (
          <View className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <Body className="text-center text-amber-800">
              {t(($) => $.promotions.edit.deactivatedNotice)}
            </Body>
          </View>
        )}
        <PromotionForm values={values} onChange={setValues} />
        <View className="gap-4">
          <Button
            variant="primary"
            size="lg"
            disabled={!isPromotionFormValid(values) || updateMutation.isPending}
            onPress={() =>
              updateMutation.mutate({
                promotionId: promotionId!,
                promotionCode: values.promotionCode.toUpperCase(),
                titleKey: values.titleKey,
                promotionAmount: Number.parseFloat(values.promotionAmount),
                promotionType: values.promotionType,
                validDuration:
                  values.promotionType === 'free_months'
                    ? null
                    : formatIntervalToPostgres(values.validDuration),
                maxActivations: values.maxActivations.trim()
                  ? Number.parseInt(values.maxActivations.trim(), 10)
                  : null,
                promotionData: buildPromotionDataFromForm(values.referralGeneration),
              })
            }>
            <ButtonLabel>
              {updateMutation.isPending
                ? t(($) => $.promotions.edit.saving)
                : t(($) => $.promotions.edit.save)}
            </ButtonLabel>
          </Button>

          {!!updateMutation.error && (
            <Body className="text-center text-destructive">
              {t(($) => $.promotions.edit.saveError)}
            </Body>
          )}
        </View>

        <View className="mt-4 gap-4 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
          <Label className="font-medium">{t(($) => $.promotions.edit.statusSection)}</Label>
          <Body className="text-sm text-neutral-600">
            {isDeactivated
              ? t(($) => $.promotions.edit.deactivatedDescription)
              : t(($) => $.promotions.edit.activeDescription)}
          </Body>
          <Button
            variant={isDeactivated ? 'outline' : 'destructive'}
            size="sm"
            disabled={isToggling}
            onPress={() =>
              isDeactivated
                ? reactivateMutation.mutate({ promotionId })
                : deactivateMutation.mutate({ promotionId })
            }>
            <ButtonLabel>
              {isToggling
                ? t(($) => $.promotions.edit.toggling)
                : isDeactivated
                  ? t(($) => $.promotions.edit.reactivateButton)
                  : t(($) => $.promotions.edit.deactivateButton)}
            </ButtonLabel>
          </Button>

          {!!(deactivateMutation.error || reactivateMutation.error) && (
            <Body className="text-center text-destructive">
              {t(($) => $.promotions.edit.toggleError)}
            </Body>
          )}
        </View>
      </ContentBox>
    </ScreenView>
  )
}
